export class Hierarchy<KEY, T> {
  parentId?: KEY;
  parents: Hierarchy<KEY, T>[];
  children: Hierarchy<KEY, T>[];
  flatChildren: Hierarchy<KEY, T>[];

  constructor(public id: KEY, public origin: T, public parent?: Hierarchy<KEY, T>) {
    this.parentId = parent?.id;
    this.parents = parent ? [...parent.parents, parent] : [];
    this.children = [];
    this.flatChildren = [];

    parent?.addChild(this);
  }

  get level() { return this.parents.length }

  includes(childOrId: Hierarchy<KEY, T> | KEY): boolean {
    if (childOrId instanceof Hierarchy) {
      return this.flatChildren.includes(childOrId);
    }
    return this.flatChildren.map(e => e.id).includes(childOrId);
  }

  checkParent(parentOrId: Hierarchy<KEY, T> | KEY) {
    if (parentOrId instanceof Hierarchy) {
      return this.parents.includes(parentOrId);
    }
    return this.parents.map(e => e.id).includes(parentOrId);
  }

  addChild(child: Hierarchy<KEY, T>) {
    if (!this.children.includes(child)) {
      this.children.push(child);
    }

    if (!this.flatChildren.includes(child)) {
      this.flatChildren.push(child);
    }

    this.parents.forEach(parent => {
      if (!parent.flatChildren.includes(child)) {
        parent.flatChildren.push(child);
      }
    });
  }

  removeChild(child: Hierarchy<KEY, T>) {
    child.children.forEach(e => child.removeChild(e));

    if (this.children.includes(child)) {
      this.children = this.children.filter(e => e !== child);
    }

    if (this.flatChildren.includes(child)) {
      this.flatChildren = this.flatChildren.filter(e => e !== child);
    }

    this.parents.forEach(parent => {
      if (parent.flatChildren.includes(child)) {
        parent.flatChildren = parent.flatChildren.filter(e => e !== child);
      }
    });
  }

  static from<KEY, T>(items: T[], idKey: string = "id", parentKey = "parent") {
    const uniqItems = Array.from(new Set(items).values());
    let heep = [...uniqItems] as (T | null)[];
    const flatHierarchies: Hierarchy<KEY, T>[] = [];

    while (heep.length !== 0) {
      heep = heep.filter(Boolean);
      [...heep].reduce((iterationResult, item, index) => {
        const [id, parentId] = [idKey, parentKey].map(key => (item as any)[key] as KEY);
        const hasParent = parentId && uniqItems.find(e => ((e as any)[idKey] as KEY) === parentId);
        const hierarchyParent = parentId && iterationResult.find(e => e.id === parentId);

        if (!hasParent || hierarchyParent) {
          heep[index] = null;
          const hierarchy = new Hierarchy<KEY, T>(id, item!, hierarchyParent);
          iterationResult.push(hierarchy);
        }
        return iterationResult;
      }, flatHierarchies);
    }

    const result = flatHierarchies.filter(e => !e.parent);
    return result;
  }
}